import {
    Component, Input
} from "@angular/core";
import { AbstractControl } from "@angular/forms";
import {
    MatIconButton
} from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { EnhancedControlDirective } from "../../../forms/directives/form-control/enhanced-control.directive";

/**
 * Clear button component for form fields. Displays a clear icon button that resets the field value.
 *
 * This component should be added to matInput and mgd-generic-autocomplete controls within mat-form-field.
 * The button is automatically hidden when the field is empty and disabled when the field is disabled.
 *
 * Use the mgdEnhancedControl directive to expose the control to the template, then pass it to the clear button.
 * For custom form controls (like mgd-generic-autocomplete), the mgdEnhancedControl directive is automatically
 * applied via hostDirectives - do NOT add it as a directive attribute in the template.
 *
 * @example
 * // Reactive Forms - Single Field
 * <mat-form-field>
 *   <input matInput formControlName="email" mgdEnhancedControl #emailControl="mgdEnhancedControl" />
 *   <mgd-clear-button matSuffix [formField]="emailControl" />
 * </mat-form-field>
 *
 * @example
 * // Reactive Forms - With FormControl
 * <mat-form-field>
 *   <input matInput [formControl]="emailControl" mgdEnhancedControl #email="mgdEnhancedControl" />
 *   <mgd-clear-button matSuffix [formField]="email" />
 * </mat-form-field>
 *
 * @example
 * // Template-Driven Forms
 * <mat-form-field>
 *   <input matInput [(ngModel)]="email" name="email" mgdEnhancedControl #emailControl="mgdEnhancedControl" />
 *   <mgd-clear-button matSuffix [formField]="emailControl" />
 * </mat-form-field>
 * @example
 * // Generic Autocomplete (mgdEnhancedControl applied automatically via hostDirectives)
 * <mat-form-field>
 *   <mat-label>Brand</mat-label>
 *   <mgd-generic-autocomplete
 *     formControlName="brand"
 *     [controller]="brandController"
 *     #brandControl="mgdEnhancedControl">
 *   </mgd-generic-autocomplete>
 *   <mgd-clear-button matSuffix [formField]="brandControl" />
 * </mat-form-field>
 * @example
 * // Multiple Fields - Date Range
 * <mat-form-field>
 *   <mat-label>Date Range: Enter a date range</mat-label>
 *   <mat-date-range-input [rangePicker]="rangePicker" mgdDateRangePicker [mgdTimezone]="rangeTimezone">
 *     <input matStartDate placeholder="Start date" formControlName="dateStart" mgdEnhancedControl #dateStartControl="mgdEnhancedControl"
 *         readonly />
 *     <input matEndDate placeholder="End date" formControlName="dateEnd" mgdEnhancedControl #dateEndControl="mgdEnhancedControl"
 *          readonly />
 *   </mat-date-range-input>
 *   <mat-hint>YYYY-MM-DD - YYYY-MM-DD</mat-hint>
 *   <mat-datepicker-toggle matSuffix [for]="rangePicker"></mat-datepicker-toggle>
 *   <mat-date-range-picker #rangePicker disabled="false"></mat-date-range-picker>
 *   <mgd-clear-button matSuffix [formField]="[dateStartControl, dateEndControl]" />
 * </mat-form-field>
 *
 * **Bulk mode** (Bulk Update Form only — see docs/angular-standards/components/04-forms.md#bulk-update-form):
 * bind `bulkIntent` to the field's clear-intent control. The button is then also visible on an
 * empty *mixed* field, a click empties the value **and** sets the intent, and the button is
 * replaced by a "Will clear for all" label with an undo button that restores `bulkRestoreValue`
 * (the `populate()` snapshot value) and drops the intent. Do not bind it on a required field.
 * Supports a single `formField`.
 *
 * @example
 * // Bulk mode
 * <mgd-clear-button matSuffix [formField]="marketAreaControl"
 *                   [bulkIntent]="marketAreaClearIntent" [bulkMixed]="isMixed('marketArea')"
 *                   [bulkRestoreValue]="mgdEnhancedForm.snapshotValueAt('marketArea')" />
 *
 * @selector mgd-clear-button
 */
@Component({
    selector: "mgd-clear-button",
    imports: [
        MatIconButton,
        MatIcon
    ],
    templateUrl: "./clear-button.component.html",
    styleUrl: "./clear-button.component.scss"
})
export class ClearButtonComponent {

    /**
     * The form control(s) to clear. Can be a single EnhancedControlDirective or an array of EnhancedControlDirectives.
     * Use mgdEnhancedControl directive to expose the control from form controls.
     *
     * @example
     * // Single field
     * [formField]="emailControl"
     *
     * @example
     * // Multiple fields
     * [formField]="[startControl, endControl]"
     */
    @Input() formField: EnhancedControlDirective | Array<EnhancedControlDirective>;

    /**
     * Bulk mode: the field's clear-intent control. Binding it opts the button into bulk mode;
     * leaving it unbound keeps the default behavior exactly as before.
     */
    @Input() bulkIntent?: AbstractControl<boolean>;

    /** Bulk mode: `true` when the selected records disagree, so the empty field can still be cleared. */
    @Input() bulkMixed = false;

    /** Bulk mode: the value undo restores — the field's `populate()` snapshot value. */
    @Input() bulkRestoreValue: unknown;

    /** Bulk mode: a clear is pending for every selected record. */
    get bulkPending(): boolean {

        return this.bulkIntent?.value === true;
    }

    /**
     * Determines if the clear button should be visible.
     * Button is shown when at least one field has a non-empty value.
     * Empty arrays are treated as empty (no value).
     * In bulk mode it is also shown on an empty mixed field, and hidden while a clear is pending.
     */
    get showButton(): boolean {
        const hasValue = (value: unknown): boolean =>
            Array.isArray(value) ? value.length > 0 : !!value;

        const fields = Array.isArray(this.formField) ? this.formField : [this.formField];
        const hasAnyValue = fields.some((field) => hasValue(field?.ngControl.value));

        if (this.bulkIntent) {
            return !this.bulkPending && (hasAnyValue || this.bulkMixed);
        }
        return hasAnyValue;
    }

    /**
     * Determines if the clear button should be disabled.
     * Button is disabled when any field is disabled.
     */
    get disabled(): boolean {
        if (Array.isArray(this.formField)) {
            return this.formField.some((field) => field.ngControl.disabled);
        }
        return this.formField?.ngControl.disabled;
    }

    /**
     * Clears the form field value(s).
     *
     * Explicitly resets to `null` rather than calling `reset()` with no argument. Angular's
     * `AbstractControl.reset()` defaults an omitted value to `this.defaultValue` — for a
     * `{ nonNullable: true }` control, that default is the control's *initial* construction
     * value, not empty. An argument-less reset on a nonNullable control therefore silently
     * restores whatever the field originally held instead of clearing it. Passing `null`
     * explicitly bypasses that fallback (it's only applied when the argument is `undefined`)
     * and clears every control uniformly, nonNullable or not.
     *
     * Does nothing if the field is disabled.
     */
    onClearClick(): void {

        if (!this.disabled) {
            if (Array.isArray(this.formField)) {
                this.formField.forEach((field) => field.ngControl.reset(null));

            }
            else {
                this.formField.ngControl.reset(null);
            }
            this.bulkIntent?.setValue(true);
        }
    }

    /**
     * Bulk mode: undoes a pending clear — restores `bulkRestoreValue` and drops the intent.
     * Does nothing if the field is disabled or bulk mode is off.
     */
    onUndoClick(): void {

        if (!this.disabled && this.bulkIntent) {
            const fields = Array.isArray(this.formField) ? this.formField : [this.formField];
            fields.forEach((field) => field.ngControl.control?.setValue(this.bulkRestoreValue ?? null));
            this.bulkIntent.setValue(false);
        }
    }
}
